import { useContext, useState, useEffect } from "react";
import { Image } from "uikit-react";
import moment from "moment";
import UIkit from "uikit";

import { AppContexts } from "../../../providers";

const WorkOrderComment = (props) => {
  const { order, readOnly = true } = props;
  const workOrderContext = useContext(AppContexts.WorkOrderContext);
  const { user } = useContext(AppContexts.AuthContext) || {};

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [preview, setPreview] = useState(null);

  const updates = order?.work_orders_updates || [];

  const canEdit = (update) => {
    // Adjust this to your permission rules:
    // for now: allow if same updater or admin (fallback: allow all if no user)
    if (!user) return true;
    const updaterId = update.updater_id || update.updater?.id;
    return user.is_admin || user.id === updaterId;
  };

  const handleEditClick = (update) => {
    if (readOnly) return;
    setEditingId(update.id);
    setEditText(update.description || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSaveEdit = async (update) => {
    if (readOnly) return;
    if (!editText.trim()) {
      UIkit.notification("Comment cannot be empty", { status: "warning" });
      return;
    }

    try {
      setSavingId(update.id);

      if (workOrderContext?.updateWorkOrderUpdate) {
        await workOrderContext.updateWorkOrderUpdate(order.id, update.id, {
          description: editText.trim(),
        });
      } else if (workOrderContext?.updateWorkOrder) {
        // Fallback: send the whole updated list back
        const updatedUpdates = updates.map((u) =>
          u.id === update.id ? { ...u, description: editText.trim() } : u
        );
        await workOrderContext.updateWorkOrder(order.id, {
          ...order,
          work_orders_updates: updatedUpdates,
        });
      }

      UIkit.notification("Comment updated", { status: "success" });
      setEditingId(null);
      setEditText("");
      setSavingId(null);

      // Optional: refetch to ensure fresh data from API
      if (workOrderContext?.getWorkOrder) {
        await workOrderContext.getWorkOrder(order.id);
      }
    } catch (err) {
      console.error(err);
      UIkit.notification("Error updating comment", { status: "danger" });
      setSavingId(null);
    }
  };

  const closePreview = () => setPreview(null);

  const goPrev = () => {
    setPreview((prev) => {
      if (!prev) return prev;
      const len = prev.attachments.length;
      const nextIndex = (prev.index - 1 + len) % len;
      return { ...prev, index: nextIndex };
    });
  };

  const goNext = () => {
    setPreview((prev) => {
      if (!prev) return prev;
      const len = prev.attachments.length;
      const nextIndex = (prev.index + 1) % len;
      return { ...prev, index: nextIndex };
    });
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setPreview(null);
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [setPreview]);

  return (
    <div className="wo-model-comment-section">
      <div className="wo-model-comment-inner">
        <h4 style={{ textTransform: "capitalize" }}>Comments</h4>
        <div className="wo-model-comment-wrapper">
          {updates.map((update) => {
            const isEditing = editingId === update.id;

            return (
              <div
                className="wo-model-comment-card wo-model-replywith-comment"
                key={`work_order_comment_${update.id}`}
              >
                <div className="wo-model-comment-content">
                  <div className="uk-form-label">
                    {`${update.updater?.first_name || ""} ${
                      update.updater?.last_name || ""
                    }`.trim() || "Unknown"}
                  </div>
                  <div className="wo-comment-date">
                    {moment.utc(update.created_on).local().format("MM/DD/YYYY, hh:mm a")}
                  </div>

                  {/* VIEW MODE */}
                  {!isEditing && (
                    <>
                      <div>{update.description}</div>

                      {!readOnly && <div className="uk-margin-bottom">
                        <button
                          type="button"
                          className="uk-button uk-button-text"
                          aria-label="Edit comment"
                          onClick={() => handleEditClick(update)}
                        >
                          <img src="/images/edit-icon.svg" />
                        </button>
                      </div>}
                    </>
                  )}

                  {/* EDIT MODE */}
                  {isEditing && !readOnly && (
                    <div className="uk-margin-small-top">
                      <textarea
                        className="uk-textarea"
                        rows={3}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                      />
                      <div
                        className="uk-margin-small-top uk-flex"
                        style={{ gap: 8 }}
                      >
                        <button
                          type="button"
                          className="uk-button hms-btn hms-red-btn"
                          onClick={() => handleSaveEdit(update)}
                          disabled={savingId === update.id}
                        >
                          {savingId === update.id ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          className="uk-button hms-btn uk-offcanvas-close"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {update.attachments &&
                    Array.isArray(update.attachments) &&
                    update.attachments.length > 0 && (
                      <div className="wo-comment-attachment uk-margin-small-top">
                        <div
                          className="uk-grid-small uk-child-width-1-4@s uk-child-width-1-6@m uk-grid"
                          data-uk-grid
                        >
                          {update.attachments.map((attachment, idx) => (
                            <div key={attachment.url} className="uk-margin-small-right">
                              <button
                                type="button"
                                className="wo-comment-thumb-btn" 
                                onClick={() =>
                                  setPreview({
                                    attachments: update.attachments,
                                    index: idx,
                                  })
                                }
                              >
                                <img
                                  src={attachment.url}
                                  alt={attachment.name}
                                  className="wo-comment-image-thumb"
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                </div>
              </div>
            );
          })}
        </div>
        {preview && (() => {
          const { attachments, index } = preview;
          const current = attachments[index];

          return (
            <div
              className="wo-image-preview"
              onClick={closePreview}
            >
              <div
                className="wo-image-preview-inner"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="wo-image-preview-close"
                  onClick={closePreview}
                >
                  ✕
                </button>

                {attachments.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="wo-image-preview-nav wo-image-preview-nav--prev"
                      onClick={goPrev}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="wo-image-preview-nav wo-image-preview-nav--next"
                      onClick={goNext}
                    >
                      ›
                    </button>
                  </>
                )}

                <img
                  src={current.url}
                  alt={current.name || "Attachment preview"}
                />
              </div>
            </div>
          );
        })()}        
      </div>
    </div>
  );
};

export default WorkOrderComment;